/**
 * Collection Chat Service
 * Handles API calls for collection conversations and messages
 */

import {
  CollectionConversation,
  CreateConversationInput,
  SendMessageInput,
  CreateConversationResult,
  SendMessageResult,
  CollectionChatMessage
} from '@/types/collectionChatTypes';
import {
  GetAccessTokenSilentlyType,
  GetAccessTokenWithPopupType
} from '@/types/transcriptionSessionTypes';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * Helper function to acquire Auth0 access token
 */
const getAuthToken = async (
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<string> => {
  const authParams = {
    authorizationParams: {
      audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
    }
  };
  try {
    return await getAccessTokenSilently(authParams);
  } catch (error: any) {
    console.warn(
      'Silent token acquisition failed for CollectionChatService:',
      error
    );
    if (
      error.error === 'consent_required' ||
      error.error === 'login_required' ||
      error.error === 'interaction_required'
    ) {
      const token = await getAccessTokenWithPopup(authParams);
      if (!token) {
        throw new Error(
          'Interactive token acquisition failed for CollectionChatService.'
        );
      }
      return token;
    }
    throw error;
  }
};

/**
 * Create a new conversation in a collection or global conversation
 * @param input - Conversation creation input (can include sessionCollectionId: null for global chats)
 * @param token - Auth token
 * @param userId - User ID
 */
export const createConversation = async (
  input: CreateConversationInput,
  token: string,
  userId: string
): Promise<CollectionConversation> => {
  try {
    // For collection-specific conversations, use collection-scoped endpoint
    // For global conversations (sessionCollectionId is null), use root endpoint
    const url = input.sessionCollectionId
      ? `${API_BASE_URL}/sessioncollections/${input.sessionCollectionId}/conversations`
      : `${API_BASE_URL}/sessioncollections/conversations`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-User-Id': userId
      },
      body: JSON.stringify(input)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to create conversation: ${response.statusText} - ${errorText}`
      );
    }

    // Backend returns the conversation object directly (not wrapped in a result)
    const conversation: CollectionConversation = await response.json();
    return conversation;
  } catch (error) {
    console.error('Error creating conversation:', error);
    throw error;
  }
};

/**
 * Get a conversation by ID (with messages)
 */
export const getConversationById = async (
  conversationId: string,
  token: string,
  userId: string
): Promise<CollectionConversation> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/sessioncollections/conversations/${conversationId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-User-Id': userId
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to get conversation: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting conversation:', error);
    throw error;
  }
};

/**
 * Send/log a message to a conversation
 * This is used to manually log messages to the database
 * Note: This is rarely used - the LangGraph agent saves messages automatically via hooks
 */
export const sendMessage = async (
  conversationId: string,
  input: Omit<SendMessageInput, 'conversationId'>,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<CollectionChatMessage> => {
  try {
    const token = await getAuthToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );

    const response = await fetch(
      `${API_BASE_URL}/sessioncollections/conversations/${conversationId}/messages`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-User-Id': userId
        },
        body: JSON.stringify({
          ...input,
          conversationId
        })
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to send message: ${response.statusText} - ${errorText}`
      );
    }

    // Backend returns the message object directly (not wrapped in a result)
    const message: CollectionChatMessage = await response.json();
    return message;
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

/**
 * Get all conversations for a collection or global conversations
 * @param collectionId - Collection ID, or null for global conversations
 * @param token - Auth token
 * @param userId - User ID
 */
export const getCollectionConversations = async (
  collectionId: string | null,
  token: string,
  userId: string
): Promise<CollectionConversation[]> => {
  try {
    let response;

    if (collectionId) {
      // Get conversations for a specific collection
      response = await fetch(
        `${API_BASE_URL}/sessioncollections/${collectionId}?includeSessions=false&includeConversations=true&includeDocuments=false`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'X-User-Id': userId
          }
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to get collection conversations: ${response.statusText}`
        );
      }

      const data = await response.json();
      return data.conversations || [];
    } else {
      // Get global conversations (where sessionCollectionId is null)
      response = await fetch(
        `${API_BASE_URL}/sessioncollections/global/conversations`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'X-User-Id': userId
          }
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to get global conversations: ${response.statusText}`
        );
      }

      return await response.json();
    }
  } catch (error) {
    console.error('Error getting collection conversations:', error);
    throw error;
  }
};

/**
 * Update the thread ID for a conversation
 */
export const updateConversationThreadId = async (
  conversationId: string,
  threadId: string,
  token: string,
  userId: string
): Promise<void> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/sessioncollections/conversations/${conversationId}/threadid`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-User-Id': userId
        },
        body: JSON.stringify({ threadId })
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to update thread ID: ${response.statusText} - ${errorText}`
      );
    }
  } catch (error) {
    console.error('Error updating thread ID:', error);
    throw error;
  }
};

/**
 * Delete a conversation
 */
export const deleteConversation = async (
  conversationId: string,
  token: string,
  userId: string
): Promise<void> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/sessioncollections/conversations/${conversationId}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-User-Id': userId
        }
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to delete conversation: ${response.statusText} - ${errorText}`
      );
    }
  } catch (error) {
    console.error('Error deleting conversation:', error);
    throw error;
  }
};
